import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FaChevronLeft, FaChevronRight, FaShieldAlt } from 'react-icons/fa'

import { get_platform_reviews } from '../store/reducers/platformReviewReducer'
import {
    ReviewCard,
    StarDisplay,
    ReviewSurfaceHeader,
} from './review-ui'
import { Skeleton } from './ui'
import { t } from 'i18next'

// Platform testimonials carousel — Phase D.
//
// Preserves: auto-rotate, prev/next, dot navigation, pause-on-hover.
// Drops:     the heavy inline <style> block; uses Tailwind + primitives.

const HomeReviews = () => {
    const dispatch = useDispatch()
    const { reviews = [], total, avgRating, ratingBreakdown = [], loader } = useSelector(s => s.platformReview)

    const [active, setActive] = useState(0)
    const [paused, setPaused] = useState(false)
    const timerRef            = useRef(null)

    useEffect(() => {
        dispatch(get_platform_reviews({ page: 1, limit: 20 }))
    }, [dispatch])

    const go = useCallback((dir) => {
        if (reviews.length < 2) return
        setActive(prev =>
            dir === 'right'
                ? (prev + 1) % reviews.length
                : (prev - 1 + reviews.length) % reviews.length
        )
    }, [reviews.length])

    useEffect(() => {
        if (paused || reviews.length === 0) return
        timerRef.current = setInterval(() => go('right'), 5000)
        return () => clearInterval(timerRef.current)
    }, [paused, reviews.length, go])

    const getCard = (offset) => {
        if (!reviews.length) return null
        return reviews[(active + offset + reviews.length) % reviews.length]
    }

    const satisfiedPct = total > 0
        ? Math.round(
              (
                  (ratingBreakdown.find(r => r.star === 5)?.count || 0) +
                  (ratingBreakdown.find(r => r.star === 4)?.count || 0)
              ) / total * 100
          )
        : 0

    return (
        <section className='w-full max-w-marketplace mx-auto px-4 sm:px-4 md-lg:px-5 md:px-5 lg:px-8 py-10 sm:py-8'>
            <ReviewSurfaceHeader
                surface='platform'
                title='İstifadəçi rəyləri – ItemZo haqqında'
                subtitle='Alıcı və satıcıların platforma haqqında fikirləri.'
                action={{ to: '/reviews', label: t('buttons.viewAll') }}
            />

            {/* Stats strip */}
            {!loader && total > 0 && (
                <div className='mb-5 grid grid-cols-3 max-w-xl rounded-card border border-slate-200 bg-white shadow-card overflow-hidden divide-x divide-slate-100'>
                    <div className='px-3 py-3 text-center'>
                        <p className='text-xl font-black text-warning-600 leading-none'>
                            {Number(avgRating || 0).toFixed(1)}
                        </p>
                        <p className='mt-0.5 text-[10px] uppercase tracking-wider text-slate-500'>Reytinq</p>
                    </div>
                    <div className='px-3 py-3 text-center'>
                        <p className='text-xl font-black text-slate-900 leading-none'>{total}</p>
                        <p className='mt-0.5 text-[10px] uppercase tracking-wider text-slate-500'>Rəy</p>
                    </div>
                    <div className='px-3 py-3 text-center'>
                        <p className='text-xl font-black text-success-600 leading-none'>%{satisfiedPct}</p>
                        <p className='mt-0.5 text-[10px] uppercase tracking-wider text-slate-500'>Məmnun</p>
                    </div>
                </div>
            )}

            {/* Cards */}
            {loader ? (
                <div className='grid grid-cols-3 md-lg:grid-cols-1 gap-4'>
                    {[0,1,2].map(i => (
                        <div key={i} className='rounded-card border border-slate-200 bg-white p-4 shadow-card'>
                            <div className='flex items-start gap-3'>
                                <Skeleton radius='full' className='h-10 w-10' />
                                <div className='flex-1'>
                                    <Skeleton radius='md' className='h-3 w-1/2' />
                                    <Skeleton radius='md' className='mt-1.5 h-2.5 w-1/3' />
                                </div>
                            </div>
                            <Skeleton.Text className='mt-3' lines={3} />
                        </div>
                    ))}
                </div>
            ) : reviews.length === 0 ? (
                <div className='rounded-card border border-dashed border-slate-200 bg-slate-50 p-8 text-center'>
                    <p className='text-3xl' aria-hidden='true'>💬</p>
                    <p className='mt-2 text-sm font-semibold text-slate-700'>
                        Hələ platforma rəyi yoxdur
                    </p>
                    <p className='mt-1 text-xs text-slate-500'>
                        İlk rəyi siz yazın və ItemZo icmasına töhfə verin.
                    </p>
                </div>
            ) : (
                <div
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                >
                    {/* 3-up carousel on desktop, 1 on tablet/mobile */}
                    <div className='grid grid-cols-3 md-lg:grid-cols-1 gap-4'>
                        {[-1, 0, 1].map((offset) => {
                            const r = getCard(offset)
                            if (!r) return null
                            const isCenter = offset === 0
                            return (
                                <div
                                    key={`${r._id || offset}-${active}`}
                                    className={`transition-all duration-300 md-lg:opacity-100 ${
                                        isCenter ? 'opacity-100' : 'opacity-60 scale-[0.97] md-lg:hidden'
                                    }`}
                                >
                                    <ReviewCard review={r} tone={isCenter ? 'platform' : undefined} />
                                </div>
                            )
                        })}
                    </div>

                    {/* Controls */}
                    {reviews.length > 1 && (
                        <div className='mt-5 flex items-center justify-center gap-3'>
                            <button
                                type='button'
                                onClick={() => go('left')}
                                aria-label='Əvvəlki rəy'
                                className='inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-card hover:border-brand-400 hover:text-brand-700 transition-colors'
                            >
                                <FaChevronLeft size={11} />
                            </button>
                            <div className='flex items-center gap-1.5'>
                                {reviews.slice(0, Math.min(reviews.length, 10)).map((_, i) => (
                                    <button
                                        key={i}
                                        type='button'
                                        onClick={() => setActive(i)}
                                        aria-label={`Rəy ${i + 1}`}
                                        className={`rounded-full transition-all ${
                                            active === i
                                                ? 'w-5 h-1.5 bg-brand-600'
                                                : 'w-1.5 h-1.5 bg-slate-200 hover:bg-slate-400'
                                        }`}
                                    />
                                ))}
                                {reviews.length > 10 && (
                                    <span className='ml-1 text-[11px] text-slate-400'>
                                        +{reviews.length - 10}
                                    </span>
                                )}
                            </div>
                            <button
                                type='button'
                                onClick={() => go('right')}
                                aria-label='Növbəti rəy'
                                className='inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-card hover:border-brand-400 hover:text-brand-700 transition-colors'
                            >
                                <FaChevronRight size={11} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Footer hint */}
            <div className='mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-400'>
                <FaShieldAlt size={10} />
                Bütün rəylər doğrulanmış istifadəçilərdəndir
                <span>•</span>
                <StarDisplay value={Number(avgRating || 0)} size={10} />
            </div>
        </section>
    )
}

export default HomeReviews
